import React, { useState, useMemo, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import StarIcon from '@mui/icons-material/Star';
import EmployeeDeploymentAvatar from './EmployeeDeploymentAvatar';


// Component for individual position circles with connectors
function PositionCircle({ width, starred = false, employees = [], deploymentId, circleId }) {
  const droppableId = `position-${deploymentId}-${circleId}`

  return (
    <div className={`flex items-center h-full ${width} px-1.5`}>
      <div className="h-1/2 w-0.5 bg-slate-300"></div>
      <div className="flex-1 h-0.5 bg-slate-300"></div>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`w-[52px] h-[52px] border-2 border-dashed rounded-full relative flex items-center justify-center ${
              snapshot.isDraggingOver
                ? 'border-violet-400 bg-violet-50'
                : 'border-slate-300'
            }`}
          >
            {starred && (
              <div className="absolute -top-[24px] left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-amber-400 rounded-b-full p-1 shadow-sm">
                  <StarIcon style={{ fontSize: "12px", color: "white" }} />
                </div>
              </div>
            )}
            {employees.length > 0 && (
              <Draggable
                draggableId={`${employees[0].first_name}-${employees[0].last_name}`}
                index={0}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <EmployeeDeploymentAvatar
                      first_name={employees[0].first_name}
                      last_name={employees[0].last_name}
                      index={employees[0].originalIndex}
                      size="small"
                      isDragging={snapshot.isDragging}
                    />
                  </div>
                )}
              </Draggable>
            )}
            {employees.length > 1 && (
              <div className="absolute -bottom-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {employees.length}
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <div className="flex-1 h-0.5 bg-slate-300"></div>
      <div className="h-1/2 w-0.5 bg-slate-300"></div>
    </div>
  )
}

// Component for role boxes
function RoleBox({ role, bgColor, textColor = "text-white" }) {
  return (
    <div className="w-[10%] h-full flex items-center justify-center px-1.5 mb-2">
      <div className={`w-full h-full ${bgColor} text-center text-xs ${textColor} font-medium flex items-center justify-center px-1 rounded`}>
        <span className="truncate">{role}</span>
      </div>
    </div>
  )
}

// Component for the roles section
function RolesSection() {
  const roles = [
    { role: "DT Order Taker", bgColor: "bg-rose-500", textColor: "text-white" },
    { role: "DT Window", bgColor: "bg-rose-500", textColor: "text-white" },
    { role: "DT Runner", bgColor: "bg-rose-500", textColor: "text-white" },
    { role: "In-Store Build", bgColor: "bg-amber-400", textColor: "text-gray-900" },
    { role: "Digital Build", bgColor: "bg-amber-400", textColor: "text-gray-900" },
    { role: "Signal Lead", bgColor: "bg-amber-400", textColor: "text-gray-900" },
    { role: "In-Store Pack", bgColor: "bg-emerald-500", textColor: "text-white" },
    { role: "Digital Pack", bgColor: "bg-emerald-500", textColor: "text-white" },
    { role: "Digital Hand Off", bgColor: "bg-emerald-500", textColor: "text-white" },
    { role: "In-Store Cashier", bgColor: "bg-emerald-500", textColor: "text-white" }
  ]

  return (
    <div className="flex-1 flex items-center h-[25%]">
      {roles.map((role, index) => (
        <RoleBox
          key={index}
          role={role.role}
          bgColor={role.bgColor}
          textColor={role.textColor}
        />
      ))}
    </div>
  )
}

// Component for deployment row
function DeploymentRow({ id, number, text = false, circleConfigs, employeeAssignments, isActive }) {
  return (
    <div className="w-full h-28 border border-slate-300 rounded-xl flex mt-3 shadow-sm bg-white overflow-hidden">
      <div className={`w-10 flex flex-col items-center justify-center text-white font-bold text-2xl transition-colors duration-300 ${
        isActive ? 'bg-green-600' : 'bg-slate-700'
      }`}>
        <p>{number}</p>
        <p className="text-xs" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          {text}
        </p>
      </div>
      <div className="flex-1">
        <div className="flex-1 flex items-center h-[75%]">
          {circleConfigs.map((config, index) => (
            <PositionCircle
              key={index}
              width={config.width}
              starred={config.starred || false}
              employees={employeeAssignments[`position-${id}-${index}`] || []}
              deploymentId={id}
              circleId={index}
            />
          ))}
        </div>
        <RolesSection />
      </div>
    </div>
  )
}


function FohDeployment({ employees }) {
  const STORAGE_KEY = 'foh-deployment-state';

  // Configuration for each deployment level with unique IDs
  const deploymentConfigs = [
    {
      id: "deploy-2",
      number: 2,
      circles: [
        { width: "w-[30%]" },
        { width: "w-[70%]", starred: true }
      ]
    },
    {
      id: "deploy-3",
      number: 3,
      circles: [
        { width: "w-[30%]" },
        { width: "w-[30%]", starred: true },
        { width: "w-[40%]" }
      ]
    },
    {
      id: "deploy-4",
      number: 4,
      circles: [
        { width: "w-[20%]" },
        { width: "w-[10%]" },
        { width: "w-[30%]", starred: true },
        { width: "w-[40%]" }
      ]
    },
    {
      id: "deploy-5",
      number: 5,
      circles: [
        { width: "w-[20%]", },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[20%]", starred: true },
        { width: "w-[40%]" }
      ]
    },
    {
      id: "deploy-6-dt",
      number: 6,
      text: "Drive-thru",
      circles: [
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[20%]", starred: true },
        { width: "w-[40%]" }
      ]
    },
    {
      id: "deploy-6-is",
      number: 6,
      text: "In-store",
      circles: [
        { width: "w-[20%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[20%]", starred: true },
        { width: "w-[20%]" },
        { width: "w-[20%]" }
      ]
    },
    {
      id: "deploy-7",
      number: 7,
      circles: [
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[20%]", starred: true },
        { width: "w-[20%]" },
        { width: "w-[20%]" },
      ]
    },
    {
      id: "deploy-8",
      number: 8,
      circles: [
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]" },
        { width: "w-[10%]", starred: true },
        { width: "w-[20%]" },
        { width: "w-[20%]" },
      ]
    },
  ]

  // Helper function to get deployment level by number
  const getDeploymentsByNumber = (number) => {
    return deploymentConfigs.filter(config => config.number === number);
  }

  // Helper function to get next higher deployment level
  const getNextHigherDeployment = (currentDeploymentId) => {
    const currentConfig = deploymentConfigs.find(config => config.id === currentDeploymentId);
    if (!currentConfig) return null;

    // Find the next higher numbered deployment
    const nextNumber = currentConfig.number + 1;
    const nextDeployments = getDeploymentsByNumber(nextNumber);

    // Return the first deployment of the next level
    return nextDeployments.length > 0 ? nextDeployments[0] : null;
  }

  // Helper function to get next lower deployment level
  const getNextLowerDeployment = (currentDeploymentId) => {
    const currentConfig = deploymentConfigs.find(config => config.id === currentDeploymentId);
    if (!currentConfig) return null;

    // Find the next lower numbered deployment
    const nextNumber = currentConfig.number - 1;
    const nextDeployments = getDeploymentsByNumber(nextNumber);

    // Return the first deployment of the next level
    return nextDeployments.length > 0 ? nextDeployments[0] : null;
  }

  // Helper function to get the other 6-person deployment
  const getOther6PersonDeployment = (currentDeploymentId) => {
    if (currentDeploymentId === "deploy-6-dt") {
      return "deploy-6-is";
    } else if (currentDeploymentId === "deploy-6-is") {
      return "deploy-6-dt";
    }
    return null;
  }

  // Helper function to check if a deployment is a 6-person deployment
  const is6PersonDeployment = (deploymentId) => {
    return deploymentId === "deploy-6-dt" || deploymentId === "deploy-6-is";
  }

  // Function to load state from localStorage
  const loadStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        // Validate that the saved state has the expected structure
        if (parsedState && typeof parsedState === 'object') {
          return parsedState;
        }
      }
    } catch (error) {
      console.error('Error loading state from localStorage:', error);
    }
    return null;
  };

  // Function to save state to localStorage
  const saveStateToStorage = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
    }
  };

  // Helper function to extract deployment ID from droppable ID
  const extractDeploymentId = (droppableId) => {
    if (!droppableId.startsWith('position-')) return null;
    // Remove 'position-' prefix and the last '-{circleId}' suffix
    const parts = droppableId.replace('position-', '').split('-');
    const circleId = parts.pop(); // Remove the last part (circle index)
    return parts.join('-'); // Rejoin the remaining parts
  };

  // Initialize state with localStorage or default values
  const [employeeAssignments, setEmployeeAssignments] = useState(() => {
    const savedState = loadStateFromStorage();

    if (savedState) {
      // Merge saved state with current employees to handle any changes
      const currentEmployeeIds = new Set(employees.map(emp => `${emp.first_name}-${emp.last_name}`));
      const mergedState = { ...savedState };

      // Remove employees that no longer exist
      Object.keys(mergedState).forEach(areaKey => {
        mergedState[areaKey] = mergedState[areaKey].filter(emp =>
          currentEmployeeIds.has(`${emp.first_name}-${emp.last_name}`)
        );
      });

      // Add new employees to non-scheduled
      const allAssignedEmployees = new Set();
      Object.values(mergedState).forEach(area => {
        area.forEach(emp => allAssignedEmployees.add(`${emp.first_name}-${emp.last_name}`));
      });

      const newEmployees = employees.filter(emp =>
        !allAssignedEmployees.has(`${emp.first_name}-${emp.last_name}`)
      ).map((emp, index) => ({ ...emp, originalIndex: index }));

      if (!mergedState['non-scheduled']) {
        mergedState['non-scheduled'] = [];
      }
      mergedState['non-scheduled'] = [...mergedState['non-scheduled'], ...newEmployees];

      return mergedState;
    }

    // Default state if no saved state exists
    return { 'non-scheduled': employees.map((emp, index) => ({ ...emp, originalIndex: index })) };
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveStateToStorage(employeeAssignments);
  }, [employeeAssignments]);

  // Function to clear saved state (useful for development/testing)
  const clearSavedState = () => {
    localStorage.removeItem(STORAGE_KEY);
    const resetState = { 'non-scheduled': employees.map((emp, index) => ({ ...emp, originalIndex: index })) };
    setEmployeeAssignments(resetState);
  };

  // Calculate active play based on deployment status
  const activePlay = useMemo(() => {
    const deploymentStatus = deploymentConfigs.map(config => {
      const totalPositions = config.circles.length
      const filledPositions = config.circles.filter((_, index) => {
        const positionKey = `position-${config.id}-${index}`
        return employeeAssignments[positionKey] && employeeAssignments[positionKey].length > 0
      }).length

      return {
        id: config.id,
        totalPositions,
        filledPositions,
        isFullyFilled: filledPositions === totalPositions,
        fillPercentage: filledPositions / totalPositions
      }
    })

    // Find deployments that are fully filled
    const fullyFilledDeployments = deploymentStatus.filter(status => status.isFullyFilled)

    if (fullyFilledDeployments.length === 0) {
      return null // No active play if nothing is fully filled
    }

    // If multiple are fully filled, return the one with more total positions
    const activeDeployment = fullyFilledDeployments.reduce((prev, current) =>
      current.totalPositions > prev.totalPositions ? current : prev
    )

    return activeDeployment.id
  }, [employeeAssignments, deploymentConfigs])

  // Add this state to force re-render and reset drag-and-drop state
  const [dragKey, setDragKey] = useState(0);

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result

    if (!destination) return

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    setEmployeeAssignments(prev => {
      const newAssignments = { ...prev }

      // Remove employee from source
      const sourceEmployees = Array.from(newAssignments[source.droppableId] || [])
      const [movedEmployee] = sourceEmployees.splice(source.index, 1)
      newAssignments[source.droppableId] = sourceEmployees

      // Add employee to destination
      const destEmployees = Array.from(newAssignments[destination.droppableId] || [])

      // For position circles, only allow one employee
      if (destination.droppableId.startsWith('position-')) {
        // If there's already someone there, switch positions instead of moving to non-scheduled
        if (destEmployees.length > 0) {
          const existingEmployee = destEmployees[0]

          // Check if source is also a position (for position-to-position switches)
          if (source.droppableId.startsWith('position-')) {
            // Switch: put existing employee in source position
            newAssignments[source.droppableId] = [existingEmployee]
          } else {
            // Source is not a position, so put existing employee back to source area
            const sourceEmployeesUpdated = Array.from(newAssignments[source.droppableId] || [])
            sourceEmployeesUpdated.splice(source.index, 0, existingEmployee)
            newAssignments[source.droppableId] = sourceEmployeesUpdated
          }
        }
        newAssignments[destination.droppableId] = [movedEmployee]
      } else {
        // For non-scheduled area, breaks, non-service-tasks, and catering-order, allow multiple employees
        destEmployees.splice(destination.index, 0, movedEmployee)
        newAssignments[destination.droppableId] = destEmployees
      }

      // Check for automatic play triggers only if there's an active play
      if (activePlay) {
        const activePlayConfig = deploymentConfigs.find(config => config.id === activePlay)

        // NEW: 6-Person Play Switch trigger
        if (destination.droppableId.startsWith('position-')) {
          const destDeploymentId = extractDeploymentId(destination.droppableId)

          // Check if we're switching between 6-person plays
          if (is6PersonDeployment(activePlay) &&
              destDeploymentId &&
              destDeploymentId !== activePlay &&
              is6PersonDeployment(destDeploymentId)) {

            // Collect all employees from the currently active 6-person play
            const activePlayEmployees = []
            activePlayConfig.circles.forEach((_, circleIndex) => {
              const positionKey = `position-${activePlay}-${circleIndex}`
              if (newAssignments[positionKey] && newAssignments[positionKey].length > 0) {
                activePlayEmployees.push(...newAssignments[positionKey])
                newAssignments[positionKey] = []
              }
            })

            // Get the target deployment config
            const targetDeploymentConfig = deploymentConfigs.find(config => config.id === destDeploymentId)

            // Clear all positions in the target deployment and move existing employees to non-scheduled
            targetDeploymentConfig.circles.forEach((_, circleIndex) => {
              const targetPositionKey = `position-${destDeploymentId}-${circleIndex}`
              // Skip the position where we just dropped the employee
              if (targetPositionKey !== destination.droppableId) {
                if (newAssignments[targetPositionKey] && newAssignments[targetPositionKey].length > 0) {
                  newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), ...newAssignments[targetPositionKey]]
                  newAssignments[targetPositionKey] = []
                }
              }
            })

            // Fill the target deployment with employees from the active play
            // (excluding the one we already moved to the destination)
            const availableEmployees = activePlayEmployees.filter(emp =>
              `${emp.first_name}-${emp.last_name}` !== draggableId
            )

            availableEmployees.forEach((employee, index) => {
              // Find the next available position (skipping the destination position)
              let targetCircleIndex = index
              if (targetCircleIndex >= destination.droppableId.split('-').pop()) {
                targetCircleIndex += 1 // Skip the position we already filled
              }

              if (targetCircleIndex < targetDeploymentConfig.circles.length) {
                const targetPositionKey = `position-${destDeploymentId}-${targetCircleIndex}`
                newAssignments[targetPositionKey] = [employee]
              } else {
                // If more employees than positions, put extras in non-scheduled
                newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), employee]
              }
            })

            // Return early to avoid other triggers
            return newAssignments
          }
        }

        // MOVE UP trigger: Adding someone from non-scheduled/other areas to a higher deployment
        if ((source.droppableId === 'non-scheduled' || source.droppableId === 'breaks' ||
             source.droppableId === 'non-service-tasks' || source.droppableId === 'catering-order') &&
            destination.droppableId.startsWith('position-')) {

          const destDeploymentId = extractDeploymentId(destination.droppableId)
          const destDeploymentConfig = deploymentConfigs.find(config => config.id === destDeploymentId)

          // Check if we're moving to a higher numbered deployment
          if (destDeploymentConfig && destDeploymentConfig.number > activePlayConfig.number) {
            // Collect all employees from the active play
            const activePlayEmployees = []
            activePlayConfig.circles.forEach((_, circleIndex) => {
              const positionKey = `position-${activePlay}-${circleIndex}`
              if (newAssignments[positionKey] && newAssignments[positionKey].length > 0) {
                activePlayEmployees.push(...newAssignments[positionKey])
                newAssignments[positionKey] = []
              }
            })

            // Find empty positions in the target deployment (excluding the one we just filled)
            const emptyPositions = []
            destDeploymentConfig.circles.forEach((_, circleIndex) => {
              const targetPositionKey = `position-${destDeploymentConfig.id}-${circleIndex}`
              // Skip the position where we just dropped the employee
              if (targetPositionKey !== destination.droppableId) {
                if (!newAssignments[targetPositionKey] || newAssignments[targetPositionKey].length === 0) {
                  emptyPositions.push(circleIndex)
                } else {
                  // If position is occupied, move existing employee to non-scheduled
                  newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), ...newAssignments[targetPositionKey]]
                  newAssignments[targetPositionKey] = []
                  emptyPositions.push(circleIndex)
                }
              }
            })

            // Fill empty positions with employees from the active play
            activePlayEmployees.forEach((employee, index) => {
              if (index < emptyPositions.length) {
                const circleIndex = emptyPositions[index]
                const targetPositionKey = `position-${destDeploymentConfig.id}-${circleIndex}`
                newAssignments[targetPositionKey] = [employee]
              } else {
                // If more employees than empty positions, put extras in non-scheduled
                newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), employee]
              }
            })
          }
        }

        // MOVE DOWN trigger: Moving someone from active play to non-scheduled/other areas
        else if (source.droppableId.startsWith('position-') &&
                 (destination.droppableId === 'non-scheduled' || destination.droppableId === 'breaks' ||
                  destination.droppableId === 'non-service-tasks' || destination.droppableId === 'catering-order')) {

          const sourceDeploymentId = extractDeploymentId(source.droppableId)

          // If moving from the active play
          if (sourceDeploymentId === activePlay) {
            const targetDeploymentConfig = getNextLowerDeployment(activePlay)

            if (targetDeploymentConfig) {
              // Collect all remaining employees from the active play (after the move)
              const activePlayEmployees = []
              activePlayConfig.circles.forEach((_, circleIndex) => {
                const positionKey = `position-${activePlay}-${circleIndex}`
                if (newAssignments[positionKey] && newAssignments[positionKey].length > 0) {
                  activePlayEmployees.push(...newAssignments[positionKey])
                  newAssignments[positionKey] = []
                }
              })

              // Move remaining active play employees to the lower deployment
              activePlayEmployees.forEach((employee, index) => {
                if (index < targetDeploymentConfig.circles.length) {
                  const targetPositionKey = `position-${targetDeploymentConfig.id}-${index}`
                  // If position is already occupied, move existing employee to non-scheduled
                  if (newAssignments[targetPositionKey] && newAssignments[targetPositionKey].length > 0) {
                    newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), ...newAssignments[targetPositionKey]]
                  }
                  newAssignments[targetPositionKey] = [employee]
                } else {
                  // If more employees than positions, put extras in non-scheduled
                  newAssignments['non-scheduled'] = [...(newAssignments['non-scheduled'] || []), employee]
                }
              })
            }
          }
        }
      }

      return newAssignments
    })

    // Force re-render of drag-and-drop context to reset internal state
    setDragKey(prev => prev + 1)
  }

  return (
    <DragDropContext key={dragKey} onDragEnd={handleDragEnd}>
      <div>
        <div className="p-6">
          {deploymentConfigs.map((config, index) => (
            <DeploymentRow
              key={config.id}
              id={config.id}
              number={config.number}
              text={config.text}
              circleConfigs={config.circles}
              employeeAssignments={employeeAssignments}
              isActive={activePlay === config.id}
            />
          ))}
          <div className="w-full flex space-x-3 mt-3">
            <div className="flex-1 border border-slate-300 rounded-lg shadow-sm bg-white overflow-hidden">
              <div className="w-full bg-slate-600 text-center text-white font-medium p-2 text-sm">
                BREAKS / DESCANSOS
              </div>
              <Droppable droppableId="breaks" direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`w-full h-20 p-2 flex flex-wrap gap-2 ${
                      snapshot.isDraggingOver ? 'bg-violet-50' : ''
                    }`}
                  >
                    {(employeeAssignments['breaks'] || []).map((employee, index) => (
                      <Draggable
                        key={`${employee.first_name}-${employee.last_name}`}
                        draggableId={`${employee.first_name}-${employee.last_name}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <EmployeeDeploymentAvatar
                              first_name={employee.first_name}
                              last_name={employee.last_name}
                              index={employee.originalIndex}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="flex-1 border border-slate-300 rounded-lg shadow-sm bg-white overflow-hidden">
              <div className="w-full bg-slate-600 text-center text-white font-medium p-2 text-sm">
                NON-SERVICE TASKS / TAREAS
              </div>
              <Droppable droppableId="non-service-tasks" direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`w-full h-20 p-2 flex flex-wrap gap-2 ${
                      snapshot.isDraggingOver ? 'bg-violet-50' : ''
                    }`}
                  >
                    {(employeeAssignments['non-service-tasks'] || []).map((employee, index) => (
                      <Draggable
                        key={`${employee.first_name}-${employee.last_name}`}
                        draggableId={`${employee.first_name}-${employee.last_name}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <EmployeeDeploymentAvatar
                              first_name={employee.first_name}
                              last_name={employee.last_name}
                              index={employee.originalIndex}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="flex-1 border border-slate-300 rounded-lg shadow-sm bg-white overflow-hidden">
              <div className="w-full bg-slate-600 text-center text-white font-medium p-2 text-sm">
                CATERING ORDER / PEDIDO DE CATERING
              </div>
              <Droppable droppableId="catering-order" direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`w-full h-20 p-2 flex flex-wrap gap-2 ${
                      snapshot.isDraggingOver ? 'bg-violet-50' : ''
                    }`}
                  >
                    {(employeeAssignments['catering-order'] || []).map((employee, index) => (
                      <Draggable
                        key={`${employee.first_name}-${employee.last_name}`}
                        draggableId={`${employee.first_name}-${employee.last_name}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <EmployeeDeploymentAvatar
                              first_name={employee.first_name}
                              last_name={employee.last_name}
                              index={employee.originalIndex}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          <div className="w-full flex space-x-3 mt-3">
            <div className="flex-1 border border-slate-300 rounded-lg shadow-sm bg-white overflow-hidden relative">
              <div className="w-full bg-slate-600 text-center text-white font-medium p-2 text-sm">
                NON-SCHEDULED / NO PROGRAMMADO
              </div>
              <Droppable droppableId="non-scheduled" direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`w-full h-24 p-2 flex flex-wrap gap-2 ${
                      snapshot.isDraggingOver ? 'bg-violet-50' : ''
                    }`}
                  >
                    {(employeeAssignments['non-scheduled'] || []).map((employee, index) => (
                      <Draggable
                        key={`${employee.first_name}-${employee.last_name}`}
                        draggableId={`${employee.first_name}-${employee.last_name}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <EmployeeDeploymentAvatar
                              first_name={employee.first_name}
                              last_name={employee.last_name}
                              index={employee.originalIndex}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              <div className="absolute bottom-2 left-2">
                <div className="flex items-center space-x-1 mt-2">
                  <div className="bg-amber-400 rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                    <StarIcon style={{ fontSize: "14px", color: "white" }} />
                  </div>
                  <p className="text-xs text-slate-600">Recommend position of PIC / Recomendar la posición del PIC</p>
                </div>
              </div>
            </div>
          </div>

          {/* Add a reset button */}
          <div className="mt-3">
            <button
              onClick={clearSavedState}
              className="px-4 py-2 bg-violet-700 text-white rounded hover:bg-violet-800 transition-colors"
            >
              Reset
            </button>
          </div>

        </div>
      </div>
    </DragDropContext>
  )
}

export default FohDeployment
